/**
 * 视频空间列表分页请求与响应归一化。
 * 封装 API 查询参数、分页常量，以及滚动加载时的去重合并与总数展示逻辑。
 */
import { videoSpaceAPI } from '@/utils/api';

/** 与后端 AdminVideoSpaceController.LIST_PAGE_SIZE 上限对齐 */
export const VIDEO_SPACE_MAX_PAGE_SIZE = 50;

/** 列表滚动加载每页条数（须 ≤ VIDEO_SPACE_MAX_PAGE_SIZE） */
export const VIDEO_SPACE_LIST_PAGE_SIZE = 30;

/** 连续空页/全重复页时停止 loadMore，避免死循环 */
export const VIDEO_SPACE_LOAD_MORE_MAX_SKIPS = 2;

/**
 * 按页拉取视频空间列表（透传筛选条件至后端）。
 *
 * @param {number} page 页码，从 1 开始
 * @param {number} [pageSize=20] 每页条数
 * @param {object} [filters] 可选筛选：startDate、endDate、ownerUserId/ownerId、source、deletedStatus
 * @returns {Promise<*>} videoSpaceAPI.getAll 的原始响应
 */
export async function fetchVideoSpacePage(page, pageSize = 20, filters = {}) {
  const query = { page, pageSize };
  const startDate = String(filters.startDate || '').trim();
  const endDate = String(filters.endDate || '').trim();
  const ownerUserId = String(filters.ownerUserId || filters.ownerId || '').trim();
  const source = String(filters.source || '').trim();
  const deletedStatus = String(filters.deletedStatus || 'active').trim().toLowerCase() || 'active';
  if (startDate) query.startDate = startDate;
  if (endDate) query.endDate = endDate;
  if (ownerUserId) query.ownerUserId = ownerUserId;
  if (source) query.source = source;
  if (deletedStatus) query.deletedStatus = deletedStatus;
  return videoSpaceAPI.getAll(query);
}

/**
 * 将多种后端分页响应形状统一为前端使用的结构。
 *
 * @param {*} res API 响应体
 * @returns {{ rows: object[], total: number, hasMore: boolean, page: number, pageSize: number, totalPages: number }}
 */
export function normalizeVideoSpacePage(res) {
  // 1. 解析嵌套 data 与 rows 数组
  const nested = res?.data && typeof res.data === 'object' && !Array.isArray(res.data)
    ? res.data
    : null;
  const rows = Array.isArray(res?.videos)
    ? res.videos
    : (Array.isArray(nested?.videos) ? nested.videos : (Array.isArray(res?.data) ? res.data : []));

  const page = Number(res?.page ?? nested?.page) || 1;
  const pageSize = Number(res?.pageSize ?? nested?.pageSize) || 20;
  const rawTotal = res?.total ?? nested?.total;
  const totalNum = Number(rawTotal);
  const totalProvided = rawTotal != null && rawTotal !== '' && Number.isFinite(totalNum) && totalNum >= 0;

  // 2. 推算安全的 total（后端 total 缺失或与当前页矛盾时的兜底）
  let safeTotal;
  if (totalProvided && totalNum > 0) {
    safeTotal = totalNum;
  } else if (totalProvided && totalNum === 0 && rows.length > 0) {
    safeTotal = rows.length;
  } else if (totalProvided) {
    safeTotal = 0;
  } else {
    safeTotal = rows.length;
  }

  // 3. 推算 hasMore
  const rawHasMore = res?.hasMore ?? nested?.hasMore;
  let hasMore;
  if (typeof rawHasMore === 'boolean') {
    hasMore = rawHasMore;
  } else if (totalProvided && safeTotal > 0) {
    hasMore = page * pageSize < safeTotal;
  } else {
    hasMore = rows.length >= pageSize;
  }

  const totalPages = Number(res?.totalPages ?? nested?.totalPages)
    || Math.max(1, Math.ceil(safeTotal / Math.max(1, pageSize)));

  return { rows, total: safeTotal, hasMore, page, pageSize, totalPages };
}

/**
 * 分页合并拉取全量视频记录，供容量统计等场景使用；单页上限与后端 LIST_PAGE_SIZE 对齐。
 *
 * @param {object} [filters] 列表筛选条件
 * @param {{ maxPages?: number }} [options] maxPages 最大请求页数，默认 200，上限 500
 * @returns {Promise<object[]>} 合并后的视频行数组
 */
export async function fetchAllVideoSpaceRowsForStorage(filters = {}, { maxPages = 200 } = {}) {
  const merged = [];
  let page = 1;
  let totalPages = 1;
  const safeMaxPages = Math.max(1, Math.min(maxPages, 500));

  while (page <= totalPages && page <= safeMaxPages) {
    const res = await fetchVideoSpacePage(page, VIDEO_SPACE_MAX_PAGE_SIZE, {
      ...filters,
      deletedStatus: filters.deletedStatus || 'all',
    });
    const normalized = normalizeVideoSpacePage(res);
    totalPages = normalized.totalPages;
    if (normalized.rows.length) {
      merged.push(...normalized.rows);
    }
    if (!normalized.hasMore && normalized.rows.length < VIDEO_SPACE_MAX_PAGE_SIZE) {
      break;
    }
    page += 1;
  }

  return merged;
}

/**
 * 将新一页 rows 按 id 去重追加到已有列表（滚动加载用）。
 *
 * @param {object[]|null|undefined} prev 已有列表
 * @param {object[]|null|undefined} rows 新一页数据
 * @returns {object[]} 合并后的列表（无新 id 时返回原引用）
 */
export function appendUniqueVideoSpaceRows(prev, rows) {
  const list = Array.isArray(prev) ? prev : [];
  const incoming = Array.isArray(rows) ? rows : [];
  if (!incoming.length) return list;
  const seen = new Set(list.map((item) => String(item?.id || '')).filter(Boolean));
  const appended = [];
  incoming.forEach((item) => {
    const id = item?.id != null ? String(item.id) : '';
    if (!id || seen.has(id)) return;
    seen.add(id);
    appended.push(item);
  });
  if (!appended.length) return list;
  return [...list, ...appended];
}

/**
 * 解析列表头部展示的「总数」文案数值（服务端 total 与已加载条数取较大可信值）。
 *
 * @param {number|string|null|undefined} listTotal 服务端返回的总数
 * @param {number} [loadedCount=0] 当前已加载条数
 * @returns {number} 用于展示的数量
 */
export function resolveVideoSpaceCountLabel(listTotal, loadedCount = 0) {
  const loaded = Math.max(0, Number(loadedCount) || 0);
  if (listTotal == null || listTotal === '') return loaded;
  const parsed = Number(listTotal);
  if (!Number.isFinite(parsed) || parsed < 0) return loaded;
  if (parsed === 0) return loaded;
  return Math.max(parsed, loaded);
}
